import { AxiosError } from 'axios';
import { t } from 'i18next';
import { useState } from 'react';
import { useTheme } from 'styled-components';

import { usersReq } from '../../../../services/requests';
import { User } from '../../../../types';

import { backendErrorNotification } from '../../../../components/Notification';
import * as s from '../../../../components/TablePaginator/styles';
import HttpStatus from '../../../../enums/httpStatus';
import ContentContainer from '../../../../templates/Content/ContentContainer';
import { ApiResPaginated } from '../../../../types/apiResponseTypes';

import AsyncTable, { Column, RequestPage } from '../../../../components/AsyncTable';
import NewRegisterBtn from '../../../../components/Buttons/NewRegisterBtn';
import { DeleteIcon, EditIcon, ResetPasswordIcon } from '../../../../icons';
import CreateOrEditUserModal from './components/CreateOrEditUserModal';
import DeleteUserModal from './components/DeleteUserModal';
import ResetPasswordModal from './components/ResetPasswordModal';

const Page1 = () => {
  const theme = useTheme();

  const [users, setUsers] = useState<User[]>([]);
  const [userSelected, setUserSelected] = useState<User | null>(null);

  const [showUserModal, setShowUserModal] = useState(false);
  const [showDeleteUserModal, setDeleteUserModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);

  const [updateTable, setUpdateTable] = useState(0);

  const handleTableEditBtn = (row: User) => {
    setUserSelected(row);
    setShowUserModal(true);
  };

  const handleNewUserBtn = () => {
    setUserSelected(null);
    setShowUserModal(true);
  };

  const handleTableDeleteBtn = (row: User) => {
    setUserSelected(row);
    setDeleteUserModal(true);
  };

  const handleResetPasswordBtn = (row: User) => {
    setUserSelected(row);
    setShowResetPasswordModal(true);
  };

  const actionsButtons = (row: User) => (
    <s.ActionButtonsContainer type={5}>
      <EditIcon onClick={() => handleTableEditBtn(row)} title={t('edit')} />
      {!row.is_tenant_admin && (
        <ResetPasswordIcon
          onClick={() => handleResetPasswordBtn(row)}
          title={t('resetPassword')}
        />
      )}
      {!row.is_tenant_admin && (
        <DeleteIcon onClick={() => handleTableDeleteBtn(row)} title={t('delete')} />
      )}
    </s.ActionButtonsContainer>
  );

  const getUsers: RequestPage = async ({ search, page, quantityPerPage }) => {
    let hasMore = true;
    const source = usersReq.axios.CancelToken.source();

    try {
      usersReq.query = `?search=${search}&page=${page}&quantityPerPage=${quantityPerPage}`;
      const res = await usersReq.index(source.token);

      if (res.status !== HttpStatus.OK) {
        throw res;
      }

      const { data, paginated }: ApiResPaginated<User[]> = res.data;

      if (data.length === 0) {
        hasMore = false;
      }

      return {
        data,
        hasMore,
        totalPage: paginated.totalPage,
      };
    } catch (err) {
      if (!usersReq.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }

      return {
        data: [],
        hasMore: false,
        totalPage: 0,
      };
    }
  };

  const handleRolesColumn = (row: User) =>
    row.roles?.map((role) => {
      if (role.name.includes('_')) {
        return (
          <p key={role.name} className="textOnTheSameLine cardListTable">
            {role.name.split('_')[1]}
          </p>
        );
      }
      return (
        <p key={role.name} className="textOnTheSameLine cardListTable">
          {role.name}
        </p>
      );
    });

  const columns: Column<User>[] = [
    {
      label: t('name') as string,
      accessor: 'name',
    },
    {
      label: t('email') as string,
      accessor: 'email',
    },
    {
      label: t('group') as string,
      Cell: handleRolesColumn,
      cellStyle: {
        display: 'flex',
        minWidth: '100%',
        overflow: 'auto',
        maxWidth: '200px',
      },
      cellClassName: 'thinScrollbar',
    },
    {
      label: t('actions') as string,
      Cell: actionsButtons,
    },
  ];

  return (
    <ContentContainer title={t('users')}>
      <NewRegisterBtn
        onClick={handleNewUserBtn}
        style={{ marginBottom: '1rem', marginLeft: '0' }}
      />

      <AsyncTable
        tableName={t('users')}
        columns={columns}
        value={users}
        onChange={setUsers}
        requestPage={getUsers}
        reqListener={[updateTable]}
        options={{
          styles: {
            primaryColor: `${theme.colors.surface}`,
            secondaryColor: `${theme.colors.onSurface}`,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
        }}
      />

      {showUserModal && (
        <CreateOrEditUserModal
          showModal={showUserModal}
          closeModal={() => setShowUserModal(false)}
          userSelected={userSelected}
          callback={() => setUpdateTable(updateTable + 1)}
        />
      )}

      {showDeleteUserModal && (
        <DeleteUserModal
          showModal={showDeleteUserModal}
          closeModal={() => setDeleteUserModal(!showDeleteUserModal)}
          userSelected={userSelected}
          reqGetAllUsers={() => setUpdateTable(updateTable + 1)}
        />
      )}

      {showResetPasswordModal && userSelected && (
        <ResetPasswordModal
          showModal={showResetPasswordModal}
          closeModal={() => setShowResetPasswordModal(false)}
          userSelected={userSelected}
          callback={() => setUpdateTable(updateTable + 1)}
        />
      )}
    </ContentContainer>
  );
};

export default Page1;
